// File: src/pages/ReceivingActivity.jsx

import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  format,
} from "date-fns";

import {
  useNavigate,
} from "react-router-dom";

import api from "@/lib/axios";

/* =============================================================================
   HELPERS
============================================================================= */

const toNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const formatQty = (value) =>
  toNumber(value).toLocaleString(undefined, {
    maximumFractionDigits: 2,
  });

const formatDateTime = (value) => {
  if (!value) {
    return "—";
  }

  const date =
    new Date(value);

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return "—";
  }

  return format(
    date,
    "HH:mm, dd MMM yyyy"
  );
};

const statusClass = (status) => {
  const normalized =
    String(status || "")
      .trim()
      .toLowerCase();

  if (
    normalized ===
    "fully received"
  ) {
    return "border-emerald-400/30 bg-emerald-500/10 text-emerald-200";
  }

  if (
    normalized ===
    "partially received"
  ) {
    return "border-amber-400/30 bg-amber-500/10 text-amber-200";
  }

  if (
    normalized ===
    "receiving"
  ) {
    return "border-cyan-400/30 bg-cyan-500/10 text-cyan-200";
  }

  return "border-slate-400/30 bg-slate-500/10 text-slate-200";
};

/* =============================================================================
   KPI CARD
============================================================================= */

function KpiCard({
  label,
  value,
  helper,
}) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
      <div className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-400">
        {label}
      </div>

      <div className="mt-2 text-2xl font-bold text-white">
        {value}
      </div>

      {helper ? (
        <div className="mt-1 text-xs text-slate-400">
          {helper}
        </div>
      ) : null}
    </div>
  );
}

/* =============================================================================
   COMPONENT
============================================================================= */

export default function ReceivingActivity() {
  const navigate =
    useNavigate();

  const [
    range,
    setRange,
  ] =
    useState("today");

  const [
    customStart,
    setCustomStart,
  ] =
    useState("");

  const [
    customEnd,
    setCustomEnd,
  ] =
    useState("");

  const [
    searchInput,
    setSearchInput,
  ] =
    useState("");

  const [
    search,
    setSearch,
  ] =
    useState("");

  const [
    rows,
    setRows,
  ] =
    useState([]);

  const [
    kpis,
    setKpis,
  ] =
    useState({
      po_count: 0,
      receipt_actions: 0,
      exception_actions: 0,
      received_quantity: "0",
      rejected_quantity: "0",
      backorders: 0,
      credits: 0,
      ignored: 0,
    });

  const [
    loading,
    setLoading,
  ] =
    useState(true);

  const [
    error,
    setError,
  ] =
    useState("");

  const [
    page,
    setPage,
  ] =
    useState(1);

  const [
    total,
    setTotal,
  ] =
    useState(0);

  const pageSize =
    25;

  /* ===========================================================================
     REQUEST
  =========================================================================== */

  const fetchActivity =
    useCallback(
      async () => {
        if (
          range === "custom" &&
          (
            !customStart ||
            !customEnd
          )
        ) {
          setRows([]);
          setTotal(0);
          setError(
            "Choose both a start date and an end date."
          );
          setLoading(false);
          return;
        }

        setLoading(true);
        setError("");

        try {
          const params = {
            range,
            page,
            page_size:
              pageSize,
          };

          if (search) {
            params.search =
              search;
          }

          if (
            range ===
            "custom"
          ) {
            params.start_date =
              customStart;

            params.end_date =
              customEnd;
          }

          const {
            data,
          } =
            await api.get(
              "/po/receiving-activity",
              {
                params,
              }
            );

          setRows(
            Array.isArray(
              data?.rows
            )
              ? data.rows
              : []
          );

          setTotal(
            toNumber(
              data?.total
            )
          );

          setKpis({
            po_count:
              toNumber(
                data?.kpis
                  ?.po_count
              ),

            receipt_actions:
              toNumber(
                data?.kpis
                  ?.receipt_actions
              ),

            exception_actions:
              toNumber(
                data?.kpis
                  ?.exception_actions
              ),

            received_quantity:
              data?.kpis
                ?.received_quantity ??
              "0",

            rejected_quantity:
              data?.kpis
                ?.rejected_quantity ??
              "0",

            backorders:
              toNumber(
                data?.kpis
                  ?.backorders
              ),

            credits:
              toNumber(
                data?.kpis
                  ?.credits
              ),

            ignored:
              toNumber(
                data?.kpis
                  ?.ignored
              ),
          });
        } catch (requestError) {
          console.error(
            "Failed to load Receiving Activity:",
            requestError
          );

          setRows([]);
          setTotal(0);

          const status =
            requestError
              ?.response
              ?.status;

          const serverMessage =
            requestError
              ?.response
              ?.data
              ?.message;

          if (
            status ===
            401
          ) {
            setError(
              "Your session is no longer valid. Please sign in again."
            );

            return;
          }

          if (
            status ===
            403
          ) {
            setError(
              "You don't have permission to view receiving activity."
            );

            return;
          }

          if (
            status ===
            400
          ) {
            setError(
              serverMessage ||
                "Receiving Activity could not be loaded because the request context is incomplete."
            );

            return;
          }

          setError(
            serverMessage ||
              "Unable to load Receiving Activity."
          );
        } finally {
          setLoading(false);
        }
      },
      [
        range,
        customStart,
        customEnd,
        search,
        page,
      ]
    );

  useEffect(() => {
    fetchActivity();
  }, [
    fetchActivity,
  ]);

  /* ===========================================================================
     ACTIONS
  =========================================================================== */

  const chooseRange =
    (nextRange) => {
      setRange(
        nextRange
      );

      setPage(
        1
      );

      setError(
        ""
      );
    };

  const handleSearch =
    (event) => {
      event.preventDefault();

      setSearch(
        searchInput.trim()
      );

      setPage(
        1
      );
    };

  const clearSearch =
    () => {
      setSearchInput(
        ""
      );

      setSearch(
        ""
      );

      setPage(
        1
      );
    };

  const totalPages =
    useMemo(
      () =>
        Math.max(
          1,
          Math.ceil(
            total /
              pageSize
          )
        ),
      [
        total,
      ]
    );

  /* ===========================================================================
     RENDER
  =========================================================================== */

  return (
    <div className="min-h-screen bg-[#0a0f24] p-4 text-white sm:p-6 lg:p-8">
      <div className="mx-auto max-w-7xl">
        {/* Header */}

        <button
          type="button"
          onClick={() =>
            navigate(
              "/po-receiving-dashboard"
            )
          }
          className="mb-6 text-sm text-cyan-400 underline transition hover:text-cyan-200"
        >
          ← Receiving Dashboard
        </button>

        <div className="mb-8">
          <div className="mb-1 text-xs font-semibold uppercase tracking-[0.16em] text-cyan-300/80">
            Goods Receiving
          </div>

          <h1 className="text-3xl font-bold">
            Receiving Activity
          </h1>

          <p className="mt-2 max-w-3xl text-sm text-slate-300">
            Operational audit of purchase orders received in this unit,
            including receipt activity, exceptions, backorders, credits,
            ignored quantities, and current PO completion.
          </p>
        </div>

        {/* Range controls */}

        <div className="mb-6 rounded-2xl border border-white/10 bg-white/5 p-4">
          <div className="mb-3 text-sm font-semibold text-slate-200">
            Reporting period
          </div>

          <div className="flex flex-wrap gap-2">
            {[
              [
                "today",
                "Today",
              ],
              [
                "7d",
                "7 Days",
              ],
              [
                "30d",
                "30 Days",
              ],
              [
                "custom",
                "Custom",
              ],
            ].map(
              ([
                value,
                label,
              ]) => (
                <button
                  key={
                    value
                  }
                  type="button"
                  onClick={() =>
                    chooseRange(
                      value
                    )
                  }
                  className={`rounded-xl border px-4 py-2 text-sm font-medium transition ${
                    range ===
                    value
                      ? "border-cyan-400/60 bg-cyan-500/20 text-cyan-100"
                      : "border-white/10 bg-white/5 text-slate-300 hover:bg-white/10"
                  }`}
                >
                  {
                    label
                  }
                </button>
              )
            )}
          </div>

          {range ===
            "custom" && (
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-400">
                  Start date
                </label>

                <input
                  type="date"
                  value={
                    customStart
                  }
                  onChange={(
                    event
                  ) => {
                    setCustomStart(
                      event
                        .target
                        .value
                    );

                    setPage(
                      1
                    );
                  }}
                  className="w-full rounded-xl border border-white/15 bg-white/10 p-3 text-white outline-none focus:border-cyan-400"
                />
              </div>

              <div>
                <label className="mb-1 block text-xs font-medium text-slate-400">
                  End date
                </label>

                <input
                  type="date"
                  value={
                    customEnd
                  }
                  onChange={(
                    event
                  ) => {
                    setCustomEnd(
                      event
                        .target
                        .value
                    );

                    setPage(
                      1
                    );
                  }}
                  className="w-full rounded-xl border border-white/15 bg-white/10 p-3 text-white outline-none focus:border-cyan-400"
                />
              </div>
            </div>
          )}
        </div>

        {/* Search */}

        <form
          onSubmit={
            handleSearch
          }
          className="mb-6 flex flex-col gap-3 rounded-2xl border border-white/10 bg-white/5 p-4 sm:flex-row"
        >
          <input
            type="text"
            value={
              searchInput
            }
            onChange={(
              event
            ) =>
              setSearchInput(
                event
                  .target
                  .value
              )
            }
            placeholder="Search PO number, supplier, item code or item name..."
            className="min-w-0 flex-1 rounded-xl border border-white/15 bg-white/10 p-3 text-white outline-none placeholder:text-slate-500 focus:border-cyan-400"
          />

          <button
            type="submit"
            className="rounded-xl bg-cyan-500 px-5 py-3 font-semibold text-black transition hover:bg-cyan-400"
          >
            Search
          </button>

          {search ? (
            <button
              type="button"
              onClick={
                clearSearch
              }
              className="rounded-xl border border-white/15 bg-white/5 px-5 py-3 text-slate-200 transition hover:bg-white/10"
            >
              Clear
            </button>
          ) : null}
        </form>

        {/* KPIs */}

        <div className="mb-8 grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-8">
          <KpiCard
            label="POs"
            value={
              kpis.po_count
            }
            helper="With activity"
          />

          <KpiCard
            label="Receipts"
            value={
              kpis.receipt_actions
            }
            helper="Receipt actions"
          />

          <KpiCard
            label="Qty Received"
            value={
              formatQty(
                kpis.received_quantity
              )
            }
          />

          <KpiCard
            label="Exceptions"
            value={
              kpis.exception_actions
            }
          />

          <KpiCard
            label="Rejected Qty"
            value={
              formatQty(
                kpis.rejected_quantity
              )
            }
          />

          <KpiCard
            label="Backorders"
            value={
              kpis.backorders
            }
          />

          <KpiCard
            label="Credits"
            value={
              kpis.credits
            }
          />

          <KpiCard
            label="Ignored"
            value={
              kpis.ignored
            }
          />
        </div>

        {/* Loading */}

        {loading ? (
          <div className="rounded-2xl border border-white/10 bg-white/5 p-8 text-center text-slate-300">
            Loading Receiving Activity...
          </div>
        ) : null}

        {/* Error */}

        {!loading &&
        error ? (
          <div className="rounded-2xl border border-red-500/30 bg-red-500/10 p-5 text-red-300">
            <div className="font-semibold">
              Receiving Activity could not be loaded
            </div>

            <div className="mt-1 text-sm">
              {
                error
              }
            </div>

            <button
              type="button"
              onClick={
                fetchActivity
              }
              className="mt-4 rounded-lg border border-red-300/30 bg-red-500/10 px-4 py-2 text-sm hover:bg-red-500/20"
            >
              Try Again
            </button>
          </div>
        ) : null}

        {/* Empty */}

        {!loading &&
        !error &&
        rows.length ===
          0 ? (
          <div className="rounded-2xl border border-white/10 bg-white/5 p-8 text-center">
            <div className="text-lg font-semibold text-slate-200">
              No receiving activity found
            </div>

            <p className="mt-2 text-sm text-slate-400">
              There are no receiving or exception transactions matching the selected period and filters.
            </p>
          </div>
        ) : null}

        {/* PO Activity Cards */}

        {!loading &&
        !error &&
        rows.length >
          0 ? (
          <div className="space-y-4">
            {rows.map(
              (
                po
              ) => {
                const completion =
                  Math.min(
                    100,
                    Math.max(
                      0,
                      toNumber(
                        po.completion_percent
                      )
                    )
                  );

                return (
                  <div
                    key={
                      po.po_id
                    }
                    className="overflow-hidden rounded-2xl border border-white/10 bg-white/5"
                  >
                    <div className="p-5">
                      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                        <div>
                          <div className="flex flex-wrap items-center gap-2">
                            <h2 className="text-xl font-bold text-white">
                              {po.po_number ||
                                `PO-${po.po_id}`}
                            </h2>

                            <span
                              className={`rounded-full border px-3 py-1 text-xs font-semibold ${statusClass(
                                po.status
                              )}`}
                            >
                              {po.status ||
                                "Unknown"}
                            </span>
                          </div>

                          <div className="mt-1 text-sm text-slate-300">
                            {po.vendor_name ||
                              "Unknown supplier"}
                          </div>

                          <div className="mt-2 text-xs text-slate-500">
                            Latest receiving activity:{" "}
                            {formatDateTime(
                              po.latest_activity_at
                            )}
                          </div>
                        </div>

                        <button
                          type="button"
                          onClick={() =>
                            navigate(
                              `/po-receiving/${po.po_id}?mode=view&log=1`
                            )
                          }
                          className="rounded-xl border border-cyan-400/30 bg-cyan-500/10 px-4 py-2 text-sm font-medium text-cyan-200 transition hover:bg-cyan-500/20"
                        >
                          Open PO →
                        </button>
                      </div>

                      {/* Lifetime quantities */}

                      <div className="mt-5 grid grid-cols-2 gap-3 md:grid-cols-4">
                        <div className="rounded-xl bg-black/20 p-3">
                          <div className="text-xs uppercase tracking-wide text-slate-500">
                            Ordered
                          </div>

                          <div className="mt-1 text-lg font-semibold">
                            {formatQty(
                              po.ordered_total
                            )}
                          </div>
                        </div>

                        <div className="rounded-xl bg-black/20 p-3">
                          <div className="text-xs uppercase tracking-wide text-slate-500">
                            Received
                          </div>

                          <div className="mt-1 text-lg font-semibold text-emerald-300">
                            {formatQty(
                              po.received_total
                            )}
                          </div>
                        </div>

                        <div className="rounded-xl bg-black/20 p-3">
                          <div className="text-xs uppercase tracking-wide text-slate-500">
                            Ignored
                          </div>

                          <div className="mt-1 text-lg font-semibold text-amber-300">
                            {formatQty(
                              po.ignored_total
                            )}
                          </div>
                        </div>

                        <div className="rounded-xl bg-black/20 p-3">
                          <div className="text-xs uppercase tracking-wide text-slate-500">
                            Remaining
                          </div>

                          <div className="mt-1 text-lg font-semibold text-cyan-300">
                            {formatQty(
                              po.remaining_total
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Completion */}

                      <div className="mt-5">
                        <div className="mb-2 flex items-center justify-between text-xs">
                          <span className="text-slate-400">
                            PO completion
                          </span>

                          <span className="font-semibold text-white">
                            {completion.toFixed(
                              1
                            )}
                            %
                          </span>
                        </div>

                        <div className="h-2 overflow-hidden rounded-full bg-white/10">
                          <div
                            className="h-full rounded-full bg-cyan-400 transition-all"
                            style={{
                              width:
                                `${completion}%`,
                            }}
                          />
                        </div>
                      </div>

                      {/* Period activity */}

                      <div className="mt-5 border-t border-white/10 pt-4">
                        <div className="mb-3 text-xs font-semibold uppercase tracking-[0.12em] text-slate-400">
                          Activity in selected period
                        </div>

                        <div className="grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-7">
                          <div>
                            <div className="text-xs text-slate-500">
                              Receipt actions
                            </div>

                            <div className="font-semibold">
                              {toNumber(
                                po.receipt_actions
                              )}
                            </div>
                          </div>

                          <div>
                            <div className="text-xs text-slate-500">
                              Qty received
                            </div>

                            <div className="font-semibold">
                              {formatQty(
                                po.received_in_period
                              )}
                            </div>
                          </div>

                          <div>
                            <div className="text-xs text-slate-500">
                              Exceptions
                            </div>

                            <div className="font-semibold">
                              {toNumber(
                                po.exception_actions
                              )}
                            </div>
                          </div>

                          <div>
                            <div className="text-xs text-slate-500">
                              Rejected qty
                            </div>

                            <div className="font-semibold">
                              {formatQty(
                                po.rejected_in_period
                              )}
                            </div>
                          </div>

                          <div>
                            <div className="text-xs text-slate-500">
                              Backorders
                            </div>

                            <div className="font-semibold">
                              {toNumber(
                                po.backorder_actions
                              )}
                            </div>
                          </div>

                          <div>
                            <div className="text-xs text-slate-500">
                              Credits
                            </div>

                            <div className="font-semibold">
                              {toNumber(
                                po.credit_actions
                              )}
                            </div>
                          </div>

                          <div>
                            <div className="text-xs text-slate-500">
                              Ignored
                            </div>

                            <div className="font-semibold">
                              {toNumber(
                                po.ignored_actions
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              }
            )}
          </div>
        ) : null}

        {/* Pagination */}

        {!loading &&
        !error &&
        total >
          pageSize ? (
          <div className="mt-8 flex flex-col gap-3 rounded-2xl border border-white/10 bg-white/5 p-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="text-sm text-slate-400">
              Page{" "}
              <span className="font-semibold text-white">
                {
                  page
                }
              </span>{" "}
              of{" "}
              <span className="font-semibold text-white">
                {
                  totalPages
                }
              </span>{" "}
              ·{" "}
              {
                total
              }{" "}
              purchase orders
            </div>

            <div className="flex gap-2">
              <button
                type="button"
                disabled={
                  page <=
                  1
                }
                onClick={() =>
                  setPage(
                    (
                      current
                    ) =>
                      Math.max(
                        1,
                        current -
                          1
                      )
                  )
                }
                className="rounded-xl border border-white/10 bg-white/5 px-4 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-40"
              >
                ← Previous
              </button>

              <button
                type="button"
                disabled={
                  page >=
                  totalPages
                }
                onClick={() =>
                  setPage(
                    (
                      current
                    ) =>
                      Math.min(
                        totalPages,
                        current +
                          1
                      )
                  )
                }
                className="rounded-xl border border-white/10 bg-white/5 px-4 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-40"
              >
                Next →
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}